import { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';

import {
    MaterialReactTable,
    MRT_ToggleDensePaddingButton,
    MRT_ToggleFiltersButton,
    MRT_ToggleFullScreenButton,
    MRT_ToggleGlobalFilterButton,
    useMaterialReactTable,
} from 'material-react-table';

import {
    Box, Chip, IconButton, Menu, MenuItem, Skeleton, Typography,
    Tooltip, Stack, ToggleButton, ToggleButtonGroup, Divider, Paper, Grid,
} from '@mui/material';

import { PieChart, Pie, Cell, Tooltip as ReTooltip, Legend, ResponsiveContainer } from 'recharts';

import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TextField from '@mui/material/TextField';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

import { mkConfig, generateCsv, download } from 'export-to-csv';

const csvConfig = mkConfig({ fieldSeparator: ',', decimalSeparator: '.', useKeysAsHeaders: true });

// ─── Helpers ──────────────────────────────────────────────────────
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
const fmtHours = (h) => h != null ? `${Number(h).toFixed(2)}h` : '—';

const get30DaysAgo = () => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
};
const getToday = () => new Date().toISOString().split('T')[0];

const getCF = (customFields = [], name) =>
    customFields.find(cf => cf.name?.toLowerCase().includes(name.toLowerCase()))?.value || null;

// ─── Couleurs charts ──────────────────────────────────────────────
const CHART_COLORS = {
    deplacement: '#f59e0b',
    commande: '#3b82f6',
    helpdesk: '#8b5cf6',
    other: '#64748b',
    yes: '#f59e0b',
    no: '#e2e8f0',
};

const TRACKER_COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#64748b'];

// ─── Card stat ────────────────────────────────────────────────────
const StatCard = ({ label, value, sub, icon, color = '#3b82f6', pct }) => (
    <Paper variant="outlined" sx={{
        p: 2, borderRadius: 2, flex: 1, minWidth: 140,
        borderColor: `${color}30`,
        background: `linear-gradient(135deg, ${color}08 0%, transparent 60%)`,
        position: 'relative', overflow: 'hidden',
    }}>
        <Box sx={{ position: 'absolute', top: 10, right: 12, opacity: 0.15, fontSize: 36, color }}>
            {icon}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>
            {label}
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, color, mt: 0.5, lineHeight: 1.2 }}>
            {value}
        </Typography>
        {pct !== undefined && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                {pct}% du total
            </Typography>
        )}
        {sub && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 11 }}>
                {sub}
            </Typography>
        )}
    </Paper>
);

// ─── Custom PieChart label ─────────────────────────────────────────
const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    if (percent < 0.05) return null;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
        <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={700}>
            {`${(percent * 100).toFixed(0)}%`}
        </text>
    );
};

// ─── Presets ──────────────────────────────────────────────────────
const PRESETS = {
    deplacement: { label: 'Déplacements/IK', icon: <DirectionsCarIcon fontSize="small" /> },
    commande: { label: 'Tracker Commande', icon: <ShoppingCartIcon fontSize="small" /> },
    helpdesk: { label: 'Tracker Helpdesk', icon: <SupportAgentIcon fontSize="small" /> },
};

function buildFilterParams(columnFilters, globalFilter, preset) {
    const params = {};
    if (globalFilter) params.search = globalFilter;
    for (const f of columnFilters) {
        const val = f.value;
        if (val === undefined || val === null || val === '') continue;
        switch (f.id) {
            case 'user': params.user = val; break;
            case 'activity': params.activity = val; break;
            case 'project': params.projectName = val; break;
            case 'organization': params.orgName = val; break;
            case 'ticket': params.ticketSubject = val; break;
            case 'comments': params.comments = val; break;
            case 'hours':
                if (Array.isArray(val)) {
                    if (val[0] != null && val[0] !== '') params.hoursMin = val[0];
                    if (val[1] != null && val[1] !== '') params.hoursMax = val[1];
                }
                break;
            case 'spentOn':
                if (Array.isArray(val)) {
                    if (val[0]) params.from = new Date(val[0]).toISOString().split('T')[0];
                    if (val[1]) params.to = new Date(val[1]).toISOString().split('T')[0];
                }
                break;
            default: break;
        }
    }
    if (preset === 'deplacement') params.deplacement = 'oui';
    if (preset === 'commande') params.trackerName = 'Commande';
    if (preset === 'helpdesk') params.trackerName = 'Helpdesk';
    return params;
}

// ─────────────────────────────────────────────────────────────────
const EntryTimeERM = ({ orgId }) => {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [total, setTotal] = useState(0);
    const [totalHours, setTotalHours] = useState(0);

    // Stats agrégées pour les charts (chargées séparément, sans pagination)
    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(true);

    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10000 });
    const [columnFilters, setColumnFilters] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [sorting, setSorting] = useState([{ id: 'spentOn', desc: true }]);
    const [preset, setPreset] = useState(null);

    // Dates par défaut : 30 derniers jours
    const [dateFrom, setDateFrom] = useState(get30DaysAgo);
    const [dateTo, setDateTo] = useState(getToday);

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    // ── Fetch tableau (paginé) ────────────────────────────────────
    const fetchEntries = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const filterParams = buildFilterParams(columnFilters, globalFilter, preset);
            const sortParam = sorting[0]?.id || 'spentOn';
            const sortDirParam = sorting[0]?.desc ? 'desc' : 'asc';
            const params = {
                page: pagination.pageIndex + 1,
                limit: pagination.pageSize,
                sort: sortParam,
                sortDir: sortDirParam,
                ...(orgId ? { orgId } : {}),
                ...(dateFrom ? { from: dateFrom } : {}),
                ...(dateTo ? { to: dateTo } : {}),
                ...filterParams,
            };
            const { data } = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/easyredmine/time-entries`,
                { params }
            );
            setEntries(data?.data || []);
            setTotal(data?.pagination?.total || 0);
            setTotalHours(data?.totalHours || 0);
        } catch (e) {
            setError(e?.response?.data?.error || e.message || 'Erreur');
        } finally {
            setLoading(false);
        }
    }, [pagination.pageIndex, pagination.pageSize, columnFilters, globalFilter, sorting, preset, orgId, dateFrom, dateTo]);

    // ── Fetch stats (pour charts, limit=all) ─────────────────────
    const fetchStats = useCallback(async () => {
        try {
            setStatsLoading(true);
            const filterParams = buildFilterParams(columnFilters, globalFilter, preset);
            const params = {
                limit: 'all',
                fields: 'hours,customFields,ticket', // hint pour le backend si supporté
                ...(orgId ? { orgId } : {}),
                ...(dateFrom ? { from: dateFrom } : {}),
                ...(dateTo ? { to: dateTo } : {}),
                ...filterParams,
            };
            const { data } = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/easyredmine/time-entries`,
                { params }
            );
            const all = data?.data || [];

            // Calcul stats déplacement
            let deplacHours = 0, nonDeplacHours = 0;
            let trackerMap = {};

            for (const e of all) {
                const isDepl = (e.customFields || []).some(
                    cf => cf.name?.toLowerCase().includes('placement') && cf.value === '1'
                );
                const h = e.hours || 0;
                if (isDepl) deplacHours += h; else nonDeplacHours += h;

                const tracker = e.ticket?.tracker || 'Sans ticket';
                trackerMap[tracker] = (trackerMap[tracker] || 0) + h;
            }

            setStats({
                deplacHours: Math.round(deplacHours * 100) / 100,
                nonDeplacHours: Math.round(nonDeplacHours * 100) / 100,
                totalHours: Math.round((deplacHours + nonDeplacHours) * 100) / 100,
                trackers: Object.entries(trackerMap)
                    .map(([name, hours]) => ({ name, hours: Math.round(hours * 100) / 100 }))
                    .sort((a, b) => b.hours - a.hours),
            });
        } catch (e) {
            console.error('Stats error', e);
        } finally {
            setStatsLoading(false);
        }
    }, [columnFilters, globalFilter, preset, orgId, dateFrom, dateTo]);

    useEffect(() => { setPagination(p => ({ ...p, pageIndex: 0 })); }, [columnFilters, globalFilter, preset, dateFrom, dateTo]);
    useEffect(() => { fetchEntries(); }, [fetchEntries]);
    useEffect(() => { fetchStats(); }, [fetchStats]);

    // ── CSV ───────────────────────────────────────────────────────
    const buildCsvRow = (e) => ({
        date: fmtDate(e.spentOn),
        utilisateur: e.user || '',
        activite: e.activity || '',
        heures: e.hours ?? 0,
        projet: e.project?.name || e.projectName || '',
        organisation: e.organization?.name || '',
        ticket: e.ticket?.subject || e.ticketSubject || '',
        commentaire: e.comments || '',
        deplacement: getCF(e.customFields, 'deplacement') || '',
    });

    const handleExportRows = (rows) => { download(csvConfig)(generateCsv(csvConfig)(rows.map(r => buildCsvRow(r.original)))); setAnchorEl(null); };
    const handleExportAll = () => { download(csvConfig)(generateCsv(csvConfig)(entries.map(buildCsvRow))); setAnchorEl(null); };

    // ── Colonnes ──────────────────────────────────────────────────
    const columns = useMemo(() => [{
        id: 'timeentry',
        columns: [
            {
                accessorFn: (row) => row.spentOn ? new Date(row.spentOn) : null,
                id: 'spentOn', header: 'Date', size: 100,
                filterVariant: 'date-range', sortingFn: 'datetime',
                Cell: ({ row }) => (
                    <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {fmtDate(row.original.spentOn)}
                    </Typography>
                ),
            },
            {
                accessorFn: (row) => row.ticket?.ticketId || '',
                id: 'ticketId', header: 'N° Ticket', size: 100, filterVariant: 'text',
                Cell: ({ renderedCellValue, row }) => {
                    if (!renderedCellValue) return <Typography variant="caption" color="text.secondary">—</Typography>;
                    return (
                        <Tooltip title={row.original.ticket?.priority || ''} placement="top">
                            <Typography variant="body2" noWrap>#{renderedCellValue}</Typography>
                        </Tooltip>
                    );
                },
            },
            { accessorKey: 'user', header: 'Utilisateur', size: 150, filterVariant: 'text' },
            {
                accessorKey: 'activity', header: 'Activité', size: 130, filterVariant: 'text',
                Cell: ({ renderedCellValue }) => (
                    <Chip label={renderedCellValue || '—'} size="small" variant="outlined" />
                ),
            },
            {
                accessorKey: 'hours', header: 'Heures', size: 90, filterVariant: 'range',
                Cell: ({ renderedCellValue }) => (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <AccessTimeIcon sx={{ fontSize: 13 }} color="action" />
                        <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                            {fmtHours(renderedCellValue)}
                        </Typography>
                    </Stack>
                ),
            },
            {
                accessorFn: (row) => row.project?.name || row.projectName || '',
                id: 'project', header: 'Projet', size: 200, filterVariant: 'text',
                Cell: ({ renderedCellValue, row }) => (
                    <Tooltip title={row.original.project?.parentProjectName || ''} placement="top">
                        <Typography variant="body2" noWrap>{renderedCellValue || '—'}</Typography>
                    </Tooltip>
                ),
            },
            {
                accessorFn: (row) => row.organization?.name || '',
                id: 'organization', header: 'Organisation', size: 150, filterVariant: 'text',
                Cell: ({ renderedCellValue }) => renderedCellValue
                    ? <Chip label={renderedCellValue} size="small" variant="outlined" />
                    : <Typography variant="caption" color="text.secondary">—</Typography>,
            },
            {
                accessorFn: (row) => row.ticket?.subject || row.ticketSubject || '',
                id: 'ticket', header: 'Ticket', size: 240, filterVariant: 'text',
                Cell: ({ renderedCellValue, row }) => {
                    if (!renderedCellValue) return <Typography variant="caption" color="text.secondary">—</Typography>;
                    return (
                        <Tooltip title={row.original.ticket?.priority || ''} placement="top">
                            <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>{renderedCellValue}</Typography>
                        </Tooltip>
                    );
                },
            },
            {
                accessorKey: 'comments', header: 'Commentaire', size: 260, filterVariant: 'text',
                Cell: ({ renderedCellValue }) => renderedCellValue
                    ? <Typography variant="body2" noWrap title={renderedCellValue} sx={{ color: 'text.secondary', maxWidth: 240 }}>{renderedCellValue}</Typography>
                    : <Typography variant="caption" color="text.secondary">—</Typography>,
            },
            {
                accessorFn: (row) => {
                    const cf = (row.customFields || []).find(c => c.name?.toLowerCase().includes('placement'));
                    return cf?.value === '1' ? 'Oui' : 'Non';
                },
                id: 'deplacement', header: 'Déplacement', size: 110,
                filterVariant: 'select', filterSelectOptions: ['Oui', 'Non'],
                enableColumnFilter: false,
                Cell: ({ renderedCellValue }) => {
                    if (renderedCellValue !== 'Oui') return <Typography variant="caption" color="text.secondary">—</Typography>;
                    return <Chip icon={<DirectionsCarIcon />} label="Oui" color="warning" size="small" />;
                },
            },
        ],
    }], []);

    const table = useMaterialReactTable({
        columns, data: entries,
        manualPagination: true, manualFiltering: true, manualSorting: true,
        enablePagination: false,
        enableGrouping: true,
        rowCount: total,
        onPaginationChange: setPagination,
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        onSortingChange: setSorting,
        state: { pagination, columnFilters, globalFilter, sorting, isLoading: loading },
        enableColumnFilterModes: true, enableColumnOrdering: true, enableColumnPinning: true,
        initialState: {
            density: 'compact',
            columnPinning: { left: ['mrt-row-expand', 'mrt-row-select'] },
            pagination: { pageSize: 10000 },
        },
        positionToolbarAlertBanner: 'bottom',
        muiSearchTextFieldProps: { size: 'small', variant: 'outlined' },
        renderToolbarInternalActions: ({ table }) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
                    <FileDownloadIcon />
                </IconButton>
                <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
                    <MenuItem disabled={table.getRowModel().rows.length === 0}
                        onClick={() => handleExportRows(table.getRowModel().rows)}>
                        Exporter la page actuelle
                    </MenuItem>
                    <MenuItem disabled={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
                        onClick={() => handleExportRows(table.getSelectedRowModel().rows)}>
                        Exporter les lignes sélectionnées
                    </MenuItem>
                    <MenuItem disabled={entries.length === 0} onClick={handleExportAll}>
                        Exporter tout (page courante)
                    </MenuItem>
                </Menu>
                <MRT_ToggleDensePaddingButton table={table} />
                <MRT_ToggleFullScreenButton table={table} />
                <MRT_ToggleFiltersButton table={table} />
                <MRT_ToggleGlobalFilterButton table={table} />
            </Box>
        ),
    });

    // ── Données charts ────────────────────────────────────────────
    const deplacData = stats ? [
        { name: 'Déplacement', value: stats.deplacHours, color: CHART_COLORS.deplacement },
        { name: 'Sur site / Remote', value: stats.nonDeplacHours, color: '#e2e8f0' },
    ] : [];

    const trackerData = stats?.trackers.slice(0, 6).map((t, i) => ({
        ...t,
        color: TRACKER_COLORS[i] || '#94a3b8',
    })) || [];

    const deplacPct = stats && stats.totalHours > 0
        ? Math.round((stats.deplacHours / stats.totalHours) * 100)
        : 0;

    return (
        <Box>
            {/* Header */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" my={1} mb={2}>
                <Typography variant="h6">Temps passé</Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <AccessTimeIcon color="action" fontSize="small" />
                    <Typography variant="body2" color="text.secondary">Total affiché :</Typography>
                    <Chip label={fmtHours(totalHours)} color="primary" size="small" icon={<AccessTimeIcon />} />
                    <Chip label={`${total} entrée${total > 1 ? 's' : ''}`} size="small" variant="outlined" />
                </Stack>
            </Stack>

            {/* Filtres prédéfinis + dates */}
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Filtres
                </Typography>
                <Divider orientation="vertical" flexItem />
                <ToggleButtonGroup size="small" value={preset} exclusive onChange={(_, val) => setPreset(val)}>
                    {Object.entries(PRESETS).map(([key, { label, icon }]) => (
                        <ToggleButton key={key} value={key} sx={{ gap: 0.5, px: 1.5 }}>
                            {icon}
                            <Typography variant="caption">{label}</Typography>
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
                {preset && (
                    <Chip label={`Filtre actif : ${PRESETS[preset].label}`} onDelete={() => setPreset(null)} size="small" color="primary" />
                )}
                <Divider orientation="vertical" flexItem />
                <Stack direction="row" alignItems="center" spacing={1}>
                    <CalendarMonthIcon fontSize="small" color="action" />
                    <TextField label="Du" type="date" size="small" value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        InputLabelProps={{ shrink: true }} sx={{ width: 145 }} />
                    <Typography variant="caption" color="text.secondary">→</Typography>
                    <TextField label="Au" type="date" size="small" value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        InputLabelProps={{ shrink: true }} sx={{ width: 145 }} />
                    {(dateFrom || dateTo) && (
                        <Chip label="Effacer dates" size="small" variant="outlined"
                            onDelete={() => { setDateFrom(''); setDateTo(''); }} />
                    )}
                </Stack>
            </Paper>

            {/* ── KPI Cards ─────────────────────────────────────── */}
            <Stack direction="row" spacing={2} mb={2} flexWrap="wrap" useFlexGap>
                <StatCard
                    label="Total heures"
                    value={fmtHours(stats?.totalHours ?? totalHours)}
                    icon={<AccessTimeIcon fontSize="inherit" />}
                    color="#3b82f6"
                />
                <StatCard
                    label="Déplacements"
                    value={statsLoading ? '…' : fmtHours(stats?.deplacHours)}
                    pct={statsLoading ? undefined : deplacPct}
                    icon={<DirectionsCarIcon fontSize="inherit" />}
                    color="#f59e0b"
                />
                {stats?.trackers.find(t => t.name?.toLowerCase().includes('commande')) && (
                    <StatCard
                        label="Tracker Commande"
                        value={fmtHours(stats.trackers.find(t => t.name?.toLowerCase().includes('commande'))?.hours)}
                        pct={stats.totalHours > 0
                            ? Math.round((stats.trackers.find(t => t.name?.toLowerCase().includes('commande'))?.hours / stats.totalHours) * 100)
                            : undefined}
                        icon={<ShoppingCartIcon fontSize="inherit" />}
                        color="#3b82f6"
                    />
                )}
                {stats?.trackers.find(t => t.name?.toLowerCase().includes('helpdesk')) && (
                    <StatCard
                        label="Tracker Helpdesk"
                        value={fmtHours(stats.trackers.find(t => t.name?.toLowerCase().includes('helpdesk'))?.hours)}
                        pct={stats.totalHours > 0
                            ? Math.round((stats.trackers.find(t => t.name?.toLowerCase().includes('helpdesk'))?.hours / stats.totalHours) * 100)
                            : undefined}
                        icon={<SupportAgentIcon fontSize="inherit" />}
                        color="#8b5cf6"
                    />
                )}
                <StatCard
                    label="Entrées"
                    value={total}
                    icon={<TrendingUpIcon fontSize="inherit" />}
                    color="#10b981"
                />
            </Stack>

            {/* ── Charts ───────────────────────────────────────────── */}
            <Grid container spacing={2} mb={2}>
                {/* Pie Déplacement */}
                <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: 240 }}>
                        <Typography variant="subtitle2" fontWeight={700} mb={1} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <DirectionsCarIcon fontSize="small" sx={{ color: '#f59e0b' }} />
                            Déplacements vs. Présentiel
                        </Typography>
                        {statsLoading ? (
                            <Skeleton variant="circular" width={160} height={160} sx={{ mx: 'auto', mt: 1 }} />
                        ) : stats?.totalHours === 0 ? (
                            <Box sx={{ textAlign: 'center', mt: 4 }}><Typography variant="caption" color="text.secondary">Aucune donnée</Typography></Box>
                        ) : (
                            <ResponsiveContainer width="100%" height={190}>
                                <PieChart>
                                    <Pie data={deplacData} cx="50%" cy="50%" innerRadius={50} outerRadius={80}
                                        dataKey="value" labelLine={false} label={renderCustomLabel}>
                                        {deplacData.map((entry, i) => (
                                            <Cell key={i} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <ReTooltip formatter={(v) => [`${Number(v).toFixed(2)}h`, '']} />
                                    <Legend iconSize={10} iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                                </PieChart>
                            </ResponsiveContainer>
                        )}
                    </Paper>
                </Grid>

                {/* Pie Trackers */}
                <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: 240 }}>
                        <Typography variant="subtitle2" fontWeight={700} mb={1} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <TrendingUpIcon fontSize="small" sx={{ color: '#3b82f6' }} />
                            Répartition par Tracker
                        </Typography>
                        {statsLoading ? (
                            <Skeleton variant="circular" width={160} height={160} sx={{ mx: 'auto', mt: 1 }} />
                        ) : trackerData.length === 0 ? (
                            <Box sx={{ textAlign: 'center', mt: 4 }}><Typography variant="caption" color="text.secondary">Aucune donnée</Typography></Box>
                        ) : (
                            <ResponsiveContainer width="100%" height={190}>
                                <PieChart>
                                    <Pie data={trackerData} cx="50%" cy="50%" innerRadius={50} outerRadius={80}
                                        dataKey="hours" nameKey="name" labelLine={false} label={renderCustomLabel}>
                                        {trackerData.map((entry, i) => (
                                            <Cell key={i} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <ReTooltip formatter={(v) => [`${Number(v).toFixed(2)}h`, '']} />
                                    <Legend iconSize={10} iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                                </PieChart>
                            </ResponsiveContainer>
                        )}
                    </Paper>
                </Grid>

                {/* Breakdown texte trackers */}
                <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: 240, overflow: 'auto' }}>
                        <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
                            Détail par Tracker
                        </Typography>
                        {statsLoading ? (
                            <Stack spacing={0.5}>{[1, 2, 3, 4].map(i => <Skeleton key={i} height={28} />)}</Stack>
                        ) : trackerData.length === 0 ? (
                            <Typography variant="caption" color="text.secondary">Aucune donnée</Typography>
                        ) : (
                            <Stack spacing={0.5}>
                                {trackerData.map((t, i) => {
                                    const pct = stats?.totalHours > 0 ? Math.round((t.hours / stats.totalHours) * 100) : 0;
                                    return (
                                        <Box key={i}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.25}>
                                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: t.color, flexShrink: 0 }} />
                                                    <Typography variant="caption" noWrap sx={{ maxWidth: 130 }}>{t.name}</Typography>
                                                </Stack>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Typography variant="caption" fontWeight={700}>{fmtHours(t.hours)}</Typography>
                                                    <Typography variant="caption" color="text.secondary">{pct}%</Typography>
                                                </Stack>
                                            </Stack>
                                            <Box sx={{ height: 4, borderRadius: 2, bgcolor: 'action.hover', overflow: 'hidden' }}>
                                                <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: t.color, borderRadius: 2, transition: 'width 0.6s ease' }} />
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Stack>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            {/* Table */}
            {error ? (
                <Box sx={{ p: 2 }}><Typography color="error">{error}</Typography></Box>
            ) : loading && entries.length === 0 ? (
                <Skeleton variant="rectangular" width="100%" height={500} />
            ) : entries.length === 0 && !loading ? (
                <Box sx={{ textAlign: 'center', p: 4 }}>
                    <Typography variant="h6" color="textSecondary">Aucune entrée de temps</Typography>
                </Box>
            ) : (
                <MaterialReactTable table={table} />
            )}
        </Box>
    );
};

export default EntryTimeERM;